import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import RolesModal from "./RolesModal";
import { useQuery, useQueryClient } from "react-query";
import { MdSearch } from "react-icons/md";
import TableComponent from "../../../CustomDataGrid";
import { Column } from "react-table";
import { Type } from "typescript";
import { FaCircle, FaPlus } from "react-icons/fa";
import { TbEyeEdit } from "react-icons/tb";
import { FunctionDescDiv } from "./styles";
import RolePermissionsService from "../../../../services/rolesPermissions.service";

/** Status da função (ativa/inativa), alinhado à coluna com bolinha verde/vermelha */
type RoleActiveFilter = "all" | "true" | "false";

const Roles = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [roleActiveFilter, setRoleActiveFilter] =
    useState<RoleActiveFilter>("all");

  const { isLoading, data } = useQuery({
    queryKey: ["roles"],
    queryFn: () => RolePermissionsService.allRoles(),
  });
  const [roleEdit, setRoleEdit] = useState<any>();
  const [showRolesModal, setShowRolesModal] = useState(false);

  const filteredRoles = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const q = search.trim().toLowerCase();

    return data.filter((role: any) => {
      const name = String(role.name ?? "").toLowerCase();
      if (q && !name.includes(q)) return false;

      if (roleActiveFilter === "true" && role.active !== true) return false;
      if (roleActiveFilter === "false" && role.active !== false) return false;

      return true;
    });
  }, [data, search, roleActiveFilter]);

  const hasRolesFromApi = (data?.length ?? 0) > 0;
  const paginationResetKey = `${search.trim()}-${roleActiveFilter}`;
  const emptyStateMessage =
    hasRolesFromApi && filteredRoles.length === 0
      ? "Nenhum resultado encontrado para os filtros aplicados."
      : undefined;

  interface IFunctionRow extends Type {
    _id: string;
    name: string;
    active: boolean;
    description: string;
    users: any[];
  }
  const columns2: Column<IFunctionRow>[] = [
    {
      Header: "FUNÇÕES",
      accessor: "name",
      Filter: "",
      Cell: ({ cell }) => {
        return (
          <FunctionDescDiv>
            <div>
              <span>
                {cell.row.original.active ? (
                  <FaCircle size={10} color="green" />
                ) : (
                  <FaCircle size={10} color="red" />
                )}{" "}
              </span>
            </div>
            <div>
              <span>{cell.row.values.name}</span>
              <br />
              <span>{cell.row.original.description}</span>
            </div>
          </FunctionDescDiv>
        );
      },
    },
    {
      Header: "USUÁRIOS",
      accessor: "users",
      Filter: "",
      Cell: ({ cell }) => {
        const list = cell.value || [];
        let activeUsers = list.filter((el: any) => el.active === true);
        let inactiveUsers = list.filter((el: any) => el.active === false);
        return (
          <span>
            {" "}
            {`${activeUsers.length} ativos; ${inactiveUsers.length} inativos`}
          </span>
        );
      },
    },
    {
      Header: "Ações",
      accessor: "_id",
      Cell: ({ cell }) => (
        <div
          onClick={() => handleFunctionEdit(cell.value)}
          style={{ cursor: "pointer" }}
        >
          <TbEyeEdit
            size={18}
            style={{
              cursor: "pointer",
              border: "1px solid #1976D2",
              padding: 2,
              borderRadius: 4,
            }}
            color="#1976D2"
          />
        </div>
      ),
      Filter: "",
      disableSortBy: true,
      width: 30,
    },
  ];

  function handleFunctionEdit(id: string) {
    let selectedFunction = (data || []).find((role: any) => role._id === id);
    if (!!selectedFunction) {
      setRoleEdit(selectedFunction);
      setShowRolesModal(true);
    }
  }

  return (
    <Box px={"24px"} pt={"24px"} pb={"28px"} sx={{ flex: 1 }}>
      {(showRolesModal || roleEdit) && (
        <RolesModal
          role={roleEdit}
          onClose={() => {
            setShowRolesModal(false);
            setRoleEdit(undefined);
          }}
          onSuccess={() => queryClient.invalidateQueries("roles")}
        />
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 20 }}>Funções</span>
        <Button
          variant="contained"
          startIcon={<FaPlus size={12} />}
          onClick={() => setShowRolesModal(true)}
        >
          Nova função
        </Button>
      </div>

      <Box display={"flex"} mb={4} alignItems={"center"} flex={1} gap={2}>
        <TextField
          size="small"
          placeholder="Pesquisar por nome da função"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <MdSearch />
              </InputAdornment>
            ),
          }}
          sx={{ width: "80%" }}
        />
        <Box display={"flex"} alignItems={"center"} flex={1} gap={2}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel id="roles-filter-status" size="small">
              Status
            </InputLabel>
            <Select
              labelId="roles-filter-status"
              label="Status"
              size="small"
              value={roleActiveFilter}
              onChange={(e) =>
                setRoleActiveFilter(e.target.value as RoleActiveFilter)
              }
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="true">Ativas</MenuItem>
              <MenuItem value="false">Inativas</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ minHeight: 400 }}>
        <TableComponent
          columns={columns2}
          isLoading={isLoading}
          data={filteredRoles}
          paginationResetKey={paginationResetKey}
          emptyStateMessage={emptyStateMessage}
        />
      </Box>
    </Box>
  );
};

export default Roles;
